import { useEffect, useState, useCallback } from "react";
import api, { formatApiErrorDetail } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { useCompany } from "@/context/CompanyContext";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import { Landmark, Send, Eye, EyeOff, Copy } from "lucide-react";
import { toast } from "sonner";
import { fmtDate } from "@/components/DatePicker";

const PILL = { sent: "bg-blue-100 text-blue-700", completed: "bg-emerald-100 text-emerald-700", expired: "bg-slate-100 text-slate-600" };

export default function DirectDeposit() {
  const { user } = useAuth();
  const { activeCompanyId } = useCompany();
  const companyId = user.role === "bureau_admin" ? activeCompanyId : user.company_id;
  const [rows, setRows] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [empId, setEmpId] = useState("");
  const [busy, setBusy] = useState(false);
  const [reveal, setReveal] = useState(null);

  const load = useCallback(() => {
    if (!companyId) return;
    const params = user.role === "bureau_admin" ? { company_id: companyId } : {};
    api.get("/direct-deposit", { params }).then((r) => setRows(r.data)).catch(() => {});
    api.get("/employees", { params }).then((r) => setEmployees(r.data)).catch(() => {});
  }, [companyId, user.role]);
  useEffect(() => { load(); }, [load]);

  const send = async () => {
    if (!empId) return;
    setBusy(true);
    try {
      const { data } = await api.post("/direct-deposit/send", { employee_id: empId });
      toast.success(`Secure form emailed to ${data.employee_email}`);
      navigator.clipboard?.writeText(data.link).catch(() => {});
      setEmpId(""); load();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); } finally { setBusy(false); }
  };

  const doReveal = async (id) => {
    try { const { data } = await api.get(`/direct-deposit/${id}/reveal`); setReveal({ id, ...data }); }
    catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };

  return (
    <div className="space-y-6 animate-fade-up" data-testid="direct-deposit-page">
      <div className="flex items-center justify-between gap-4 flex-wrap">
        <div>
          <h1 className="font-display text-2xl sm:text-3xl font-bold tracking-tight">Direct Deposit</h1>
          <p className="text-sm text-muted-foreground mt-1">Send employees a secure, one-time link to complete their bank authorization. Numbers are encrypted at rest.</p>
        </div>
        <div className="flex gap-2 items-center">
          <Select value={empId} onValueChange={setEmpId}>
            <SelectTrigger data-testid="dd-employee-select" className="w-64"><SelectValue placeholder="Choose employee…" /></SelectTrigger>
            <SelectContent>{employees.map((e) => <SelectItem key={e.id} value={e.id}>{e.name} {e.direct_deposit_on_file ? "✓" : ""}</SelectItem>)}</SelectContent>
          </Select>
          <Button data-testid="dd-send-btn" onClick={send} disabled={!empId || busy}><Send className="w-4 h-4 mr-1.5" /> {busy ? "Sending…" : "Send Secure Form"}</Button>
        </div>
      </div>

      <Card className="overflow-hidden">
        {rows.length === 0 ? <div className="p-10 text-center text-muted-foreground"><Landmark className="w-8 h-8 mx-auto mb-2 opacity-50" />No direct deposit forms sent yet.</div> : (
          <table className="w-full text-sm">
            <thead className="text-xs text-muted-foreground text-left bg-muted/50"><tr><th className="p-3">Employee</th><th className="p-3">Sent</th><th className="p-3">Status</th><th className="p-3">Accounts (masked)</th><th className="p-3">Completed</th><th className="p-3"></th></tr></thead>
            <tbody>
              {rows.map((r) => (
                <tr key={r.id} data-testid={`dd-row-${r.id}`} className="border-t border-border">
                  <td className="p-3"><div className="font-medium">{r.employee_name}</div><div className="text-xs text-muted-foreground">{r.employee_email}</div></td>
                  <td className="p-3 text-xs">{fmtDate(r.sent_at)}<div className="text-muted-foreground">by {r.sent_by}</div></td>
                  <td className="p-3"><span data-testid={`dd-status-${r.status}`} className={`px-2 py-0.5 rounded-md text-xs font-semibold capitalize ${PILL[r.status]}`}>{r.status}</span></td>
                  <td className="p-3 text-xs font-mono-num">{r.accounts.map((a, i) => <div key={i}>{a.bank_name} · {a.account_type} ····{a.account_last4} · {a.allocation_type === "remainder" ? "remainder" : a.allocation_type === "percent" ? `${a.allocation_value}%` : `$${a.allocation_value}`}</div>)}{r.accounts.length === 0 && "—"}</td>
                  <td className="p-3 text-xs">{r.completed_at ? fmtDate(r.completed_at) : "—"}</td>
                  <td className="p-3 text-right">{r.status === "completed" && <Button size="sm" variant="outline" data-testid={`dd-reveal-${r.id}`} onClick={() => doReveal(r.id)}><Eye className="w-3.5 h-3.5 mr-1" /> Reveal</Button>}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </Card>

      <Dialog open={!!reveal} onOpenChange={(o) => !o && setReveal(null)}>
        <DialogContent data-testid="dd-reveal-modal">
          <DialogHeader><DialogTitle className="font-display flex items-center gap-2"><EyeOff className="w-4 h-4" /> Full banking details</DialogTitle><DialogDescription>Access is logged. Use only to set up payroll deposits.</DialogDescription></DialogHeader>
          {reveal && (
            <div className="space-y-3">
              {reveal.accounts.map((a, i) => (
                <div key={i} className="rounded-lg border border-border p-3 text-sm font-mono-num space-y-1">
                  <div className="font-sans font-semibold">{a.bank_name} · <span className="capitalize">{a.account_type}</span></div>
                  <div className="flex justify-between"><span className="text-muted-foreground">Routing</span><span data-testid={`dd-full-routing-${i}`}>{a.routing_number} <button onClick={() => { navigator.clipboard.writeText(a.routing_number); toast.success("Copied"); }}><Copy className="inline w-3 h-3" /></button></span></div>
                  <div className="flex justify-between"><span className="text-muted-foreground">Account</span><span data-testid={`dd-full-account-${i}`}>{a.account_number} <button onClick={() => { navigator.clipboard.writeText(a.account_number); toast.success("Copied"); }}><Copy className="inline w-3 h-3" /></button></span></div>
                  <div className="flex justify-between"><span className="text-muted-foreground">Allocation</span><span>{a.allocation_type === "remainder" ? "Remainder" : a.allocation_type === "percent" ? `${a.allocation_value}%` : `$${a.allocation_value}`}</span></div>
                </div>
              ))}
              {reveal.signature && <div className="flex items-center gap-3 text-xs text-muted-foreground"><img src={reveal.signature} alt="signature" className="h-12 bg-white border border-border rounded px-2" /> Signed by {reveal.signed_name} on {fmtDate(reveal.completed_at)}</div>}
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
